import { InputSelect, InputWrapper } from '@vrplatform/components';
import type { SelectItem } from '@vrplatform/components';
import { useQuery, useTeam } from '@vrplatform/data';
import { sortBy } from '@vrplatform/utils';
import { Box } from '@mantine/core';
import { VRP_TENANT_ID } from '@vrplatform/ui-common';
import { Controller } from 'react-hook-form';
import { type FeeFormInputs, useFeeForm } from '../useFeeForm';

export const STORYBOOK_FEE_BOOKING_CHANNELS_QUERY_KEY =
  'fee-booking-channels-editor';

export const FeeBookingChannelEditor = () => {
  const methods = useFeeForm();

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing.xs,
      })}
    >
      <Controller
        control={methods.control}
        name="bookingChannelsFilter"
        rules={{
          validate: (value) => {
            if (value === 'all-channels' || (value && value.length > 0))
              return true;

            return 'Please select at least one booking channel';
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <Box
              miw={300}
              sx={{
                flex: 1,
              }}
            >
              <InputWrapper error={fieldState.error?.message}>
                <MultiSelect
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!fieldState.error}
                  disabled={field.disabled}
                />
              </InputWrapper>
            </Box>
          );
        }}
      />
    </Box>
  );
};

const MultiSelect = ({
  onChange,
  value: initial,
  hasError,
  disabled,
}: {
  value: FeeFormInputs['bookingChannelsFilter'] | undefined;
  onChange: (value: FeeFormInputs['bookingChannelsFilter']) => void;
  hasError: boolean;
  disabled: boolean | undefined;
}) => {
  const {
    data: options = [],
    isInitialLoading: loading,
    error,
  } = useBookingChannelQuery();

  const value: SelectItem[] =
    initial === 'all-channels'
      ? [{ value: 'all-channels', label: 'All channels' }]
      : options.filter((x) => initial?.includes(x.value));

  return (
    <InputSelect
      data-testid="fee-booking-channel-editor-select"
      type="multiple"
      value={value}
      setValue={(bookingChannels) => {
        onChange(bookingChannels.map((x) => x.value));
      }}
      inputProps={{
        error: hasError,
        loadingQuery: loading,
        placeholder: 'Select booking channels',
        withClearButton: true,
        disabled,
      }}
      data={{
        options: options || [],
        loading,
        error,
        sort: null,
      }}
      dropdownProps={{
        preventCloseOnSelect: true,
        width: 400,
      }}
      customActionTop={{
        label: 'All channels',
        onSubmit: () => {
          onChange('all-channels');
        },
      }}
    />
  );
};

function useBookingChannelQuery() {
  const [{ id: teamId }] = useTeam();

  const isVrpTeam = teamId === VRP_TENANT_ID;

  return useQuery(
    (q, args) => {
      return sortBy(
        q
          .bookingChannels({
            order_by: [
              {
                channelRef: 'asc_nulls_last',
              },
            ],
            distinct_on: ['channelRef'],
            where: {
              channelRef: { _is_null: false },
              reservations: args.isVrpTeam
                ? undefined
                : {
                    tenantId: { _eq: args.teamId },
                  },
            },
          })
          .map<SelectItem>((channel) => {
            const ref = channel.channelRef || '-';

            return {
              label: ref.trim(),
              value: ref,
            };
          }),
        'label',
        'asc'
      );
    },
    {
      keepPreviousData: true,
      storybookQueryKey: STORYBOOK_FEE_BOOKING_CHANNELS_QUERY_KEY,
      variables: {
        teamId,
        isVrpTeam,
      },
    }
  );
}
